/**
 * Macro tree lock contracts.
 *
 * `PUT /admin/:campaignId/macro-tree` accepts the authored graph plus the
 * draft revision it was saved at. The server resolves analysis itself: it
 * projects the graph to its root-reachable execution shape, resolves the
 * analysis request bound to that revision/projection, and either attaches a
 * complete hardware calibration (`calibrated`) or writes the exact empty
 * graph as a deliberate disabled revision (`disabled`).
 *
 * There is no client-selected calibration handle any more: the designer never
 * chooses a job or artifact, and the gate never trusts one from the wire.
 *
 * A graph that carries any node unreachable from its root is refused with
 * `disconnected_components` before analysis is resolved — those branches are
 * never analyzed or executed until the author connects them.
 *
 * `projectionDigest` is the canonical digest of the projection, computed by
 * the same identity service used everywhere else; no other hash is used.
 */
import { z } from 'zod';
import { macroVisualDigestSchema } from './macroCalibration';
import { lockedMacroTreeGraphSchema, macroTreeGraphAuthoringSchema } from './macroTree';

const jobId = z.string().min(1).max(128);
const pendingJobId = jobId.nullable();
const draftRev = z.number().int().nonnegative();

export const lockErrorCodeSchema = z.enum([
  'analysis_pending', 'analysis_failed', 'analysis_stale', 'artifact_missing',
  'artifact_incomplete', 'artifact_not_hardware', 'artifact_wrong_target', 'attachment_conflict',
  'disconnected_components',
]);
export type LockErrorCode = z.infer<typeof lockErrorCodeSchema>;

/** Calibrated lock result: the artifact attached to the committed revision. */
export const lockCalibrationResultSchema = z.object({
  artifactDigest: macroVisualDigestSchema,
  manifestDigest: macroVisualDigestSchema,
  workPlanDigest: macroVisualDigestSchema,
  projectionDigest: macroVisualDigestSchema,
}).strict();
export type LockCalibrationResult = z.infer<typeof lockCalibrationResultSchema>;

/**
 * Lock success is explicitly discriminated. `disabled` is a first-class,
 * successful lock of the exact empty launch graph — it supersedes any prior
 * macro and produces `no_macro` at runtime; it carries no calibration.
 */
export const lockExecutionResultSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('disabled') }).strict(),
  z.object({
    mode: z.literal('calibrated'),
    calibration: lockCalibrationResultSchema,
  }).strict(),
]);
export type LockExecutionResult = z.infer<typeof lockExecutionResultSchema>;

/** The only lock request: the authored graph and the draft revision it came from. */
export const macroTreeLockRequestSchema = z.object({
  graph: macroTreeGraphAuthoringSchema,
  draftRev,
}).strict();
export type MacroTreeLockRequest = z.infer<typeof macroTreeLockRequestSchema>;

/** 202 — the revision's analysis is still running; the caller follows this draft revision. */
export const lockAnalysisPendingSchema = z.object({
  outcome: z.literal('analysis_pending'),
  jobId: pendingJobId,
  draftRev,
  expectedLockedRev: z.number().int().positive(),
  projectionDigest: macroVisualDigestSchema,
}).strict();
export type LockAnalysisPending = z.infer<typeof lockAnalysisPendingSchema>;

/** 422 — analysis finished but produced no usable artifact. */
export const lockAnalysisFailedSchema = z.object({
  outcome: z.literal('analysis_failed'),
  jobId: pendingJobId,
  errorCode: z.string().min(1).max(128),
  referenceIds: z.array(z.string()).max(64),
}).strict();
export type LockAnalysisFailed = z.infer<typeof lockAnalysisFailedSchema>;

/** 422 — or 409 for `attachment_conflict`. */
export const lockRejectedSchema = z.object({
  outcome: z.literal('rejected'),
  errorCode: lockErrorCodeSchema,
  details: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
}).strict();
export type LockRejected = z.infer<typeof lockRejectedSchema>;

/** Every non-success outcome, discriminated by `outcome`. */
export const macroTreeLockFailureSchema = z.discriminatedUnion('outcome', [
  lockAnalysisPendingSchema, lockAnalysisFailedSchema, lockRejectedSchema,
]);
export type MacroTreeLockFailure = z.infer<typeof macroTreeLockFailureSchema>;

/** 200 — the committed graph is exactly the empty (disabled) or a strict execution graph. */
export const macroTreeLockSuccessSchema = z.object({
  success: z.literal(true),
  data: lockedMacroTreeGraphSchema,
  rev: z.number().int().positive(),
  execution: lockExecutionResultSchema,
}).strict();
export type MacroTreeLockSuccess = z.infer<typeof macroTreeLockSuccessSchema>;

export const macroTreeLockResponseSchema = z.union([macroTreeLockSuccessSchema, macroTreeLockFailureSchema]);
export type MacroTreeLockResponse = z.infer<typeof macroTreeLockResponseSchema>;
